/**
 * PainAssessmentInput Component
 * 
 * 0-10 slider with pain description field for clinical history documentation.
 */

import React from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  Stack,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

interface PainAssessmentInputProps {
  painSeverity: number;
  painDescription: string;
  onPainSeverityChange: (value: number) => void;
  onPainDescriptionChange: (value: string) => void;
  disabled?: boolean;
}

const SEVERITY_LABELS: Record<number, { label: string; color: string }> = {
  0: { label: 'No Pain', color: '#22c55e' },
  1: { label: 'Minimal', color: '#84cc16' },
  2: { label: 'Mild', color: '#a3e635' },
  3: { label: 'Uncomfortable', color: '#facc15' },
  4: { label: 'Moderate', color: '#fbbf24' },
  5: { label: 'Distracting', color: '#f97316' },
  6: { label: 'Distressing', color: '#fb923c' },
  7: { label: 'Unmanageable', color: '#ef4444' },
  8: { label: 'Intense', color: '#dc2626' },
  9: { label: 'Severe', color: '#b91c1c' },
  10: { label: 'Unbearable', color: '#991b1b' },
};

const PAIN_DESCRIPTION_SUGGESTIONS = [
  'aching',
  'burning',
  'radiating',
  'sharp',
  'stabbing',
  'throbbing',
  'constant',
  'intermittent',
  'worsening',
];

const marks = [
  { value: 0, label: '0' },
  { value: 2, label: '2' },
  { value: 4, label: '4' },
  { value: 6, label: '6' },
  { value: 8, label: '8' },
  { value: 10, label: '10' },
];

export const PainAssessmentInput: React.FC<PainAssessmentInputProps> = ({
  painSeverity,
  painDescription,
  onPainSeverityChange,
  onPainDescriptionChange,
  disabled = false,
}) => {
  const severityInfo = SEVERITY_LABELS[painSeverity] ?? SEVERITY_LABELS[5];

  const handleSliderChange = (_: Event, value: number | number[]) => {
    onPainSeverityChange(value as number);
  };

  const addDescriptor = (descriptor: string) => {
    const current = painDescription.trim();
    if (current.toLowerCase().includes(descriptor.toLowerCase())) return;
    
    const newDescription = current 
      ? `${current}, ${descriptor}`
      : descriptor;
    onPainDescriptionChange(newDescription);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Pain Assessment
        </Typography>
        <Tooltip title="Document pain severity (0-10 scale) and character. High severity scores (7+) strengthen medical necessity arguments.">
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Pain Severity Slider */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Pain Severity
            </Typography>
            <Chip
              label={`${painSeverity}/10 - ${severityInfo.label}`}
              size="small"
              sx={{
                backgroundColor: severityInfo.color,
                color: painSeverity >= 5 ? 'white' : 'inherit',
                fontWeight: 600,
              }}
            />
          </Stack>
          <Box sx={{ px: 1 }}>
            <Slider
              value={painSeverity}
              onChange={handleSliderChange}
              min={0}
              max={10}
              step={1}
              marks={marks}
              disabled={disabled}
              sx={{
                '& .MuiSlider-thumb': {
                  backgroundColor: severityInfo.color,
                },
                '& .MuiSlider-track': {
                  backgroundColor: severityInfo.color,
                },
                '& .MuiSlider-rail': {
                  background: 'linear-gradient(to right, #22c55e, #facc15, #ef4444)',
                },
              }}
            />
          </Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="textSecondary">
              No Pain
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Worst Pain
            </Typography>
          </Stack>
        </Box>

        {/* Pain Description */}
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Pain Description
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Describe the pain character, location, and radiation (e.g., 'severe aching pain in lower back radiating to bilateral lower extremities')"
            value={painDescription}
            onChange={(e) => onPainDescriptionChange(e.target.value)}
            disabled={disabled}
            size="small"
            sx={{ mb: 1 }}
          />
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
              Quick add:
            </Typography>
            {PAIN_DESCRIPTION_SUGGESTIONS.map((descriptor) => (
              <Chip
                key={descriptor}
                label={descriptor}
                size="small"
                variant="outlined"
                onClick={() => addDescriptor(descriptor)}
                disabled={disabled}
                sx={{
                  mr: 0.5,
                  mb: 0.5,
                  cursor: 'pointer',
                  fontSize: '0.7rem',
                }}
              />
            ))}
          </Box>
        </Box>
      </Paper>

      {painSeverity >= 7 && (
        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
          ✓ High pain severity (7+) strongly supports medical necessity
        </Typography>
      )}
    </Box>
  );
};
